/**
 * API Service
 * Handles all API communications with the AWL backend.
 * Uses localStorage token in dev mode, Cognito in production.
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const TOKEN_KEY = 'awl_token';
const REFRESH_TOKEN_KEY = 'awl_refresh_token';

class ApiService {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

  constructor() {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const gcpApiKey = import.meta.env.VITE_GCP_API_KEY;
    if (gcpApiKey) {
      headers['x-api-key'] = gcpApiKey;
    }

    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers,
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      async (config) => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: attempt token refresh on 401
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't retry auth endpoints or already-retried requests
        if (
          error.response?.status !== 401 ||
          originalRequest._retry ||
          originalRequest.url?.startsWith('/auth/')
        ) {
          return Promise.reject(error);
        }

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          this.clearAuthAndRedirect();
          return Promise.reject(error);
        }

        // If a refresh is already in progress, queue this request
        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.refreshQueue.push({
              resolve: (token: string) => {
                originalRequest._retry = true;
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.client(originalRequest));
              },
              reject,
            });
          });
        }

        this.isRefreshing = true;
        originalRequest._retry = true;

        try {
          const response = await this.client.post('/auth/refresh', { refreshToken });
          const data = response.data.data;
          const newToken = data.tokens?.accessToken || data.token;

          localStorage.setItem(TOKEN_KEY, newToken);

          // Retry all queued requests with the new token
          this.refreshQueue.forEach((pending) => pending.resolve(newToken));
          this.refreshQueue = [];

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return this.client(originalRequest);
        } catch {
          this.refreshQueue.forEach((pending) => pending.reject(error));
          this.refreshQueue = [];
          this.clearAuthAndRedirect();
          return Promise.reject(error);
        } finally {
          this.isRefreshing = false;
        }
      }
    );
  }

  private clearAuthAndRedirect() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('awl_user');
    window.location.href = '/login';
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get(url, config);
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post(url, data, config);
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete(url, config);
  }
}

const api = new ApiService();
export default api;
